import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "@normasteaching/norma-ui";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { Card } from "../../UI/Card.tsx";
import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import {
  AssignmentKind,
  AssignmentTraceDTO,
  AssignmentsDTO,
  useGetSchedulerAssignments,
} from "../../HubSpot/useGetSchedulerAssignments.ts";
import { CheckClosersAvailabilityScreenPath } from "../CheckClosersAvailabilityScreen.tsx";
import { PeriodSelector } from "../Stats/PeriodSelector.tsx";
import { StatsKpi } from "../Stats/StatsKpi.tsx";
import {
  ASSIGNMENT_PERIOD_OPTIONS,
  presetRange,
  rangeError,
  rangeIncludingFuture,
  StatsPeriod,
} from "../Stats/statsPeriod.ts";
import {
  AssignmentOutcome,
  CHANNEL_LABELS,
  KIND_LABELS,
  outcomeOf,
} from "./assignmentOutcome.ts";
import { AssignmentsHeatmap, SlotSelection } from "./AssignmentsHeatmap.tsx";
import { AssignmentSlotModal } from "./AssignmentSlotModal.tsx";

type ViewFilter = "current" | "history";
type ChannelFilter = "all" | AssignmentTraceDTO["channel"];
type KindFilter = "all" | AssignmentKind;

const VIEW_OPTIONS: { id: ViewFilter; name: string }[] = [
  { id: "current", name: "Assegnazioni attuali" },
  { id: "history", name: "Tutta la cronologia" },
];

const CHANNEL_OPTIONS: { id: ChannelFilter; name: string }[] = [
  { id: "all", name: "Tutti i canali" },
  { id: "web", name: CHANNEL_LABELS.web },
  { id: "setter", name: CHANNEL_LABELS.setter },
];

const KIND_OPTIONS: { id: KindFilter; name: string }[] = [
  { id: "all", name: "Tutte" },
  ...(Object.keys(KIND_LABELS) as AssignmentKind[]).map((kind) => ({
    id: kind,
    name: KIND_LABELS[kind],
  })),
];

function countOutcome(
  traces: AssignmentTraceDTO[],
  outcome: AssignmentOutcome,
): number {
  return traces.filter((t) => outcomeOf(t) === outcome).length;
}

export function AssignmentsScreen() {
  const navigate = useNavigate();
  const { getSchedulerAssignments, loading } = useGetSchedulerAssignments();
  const [period, setPeriod] = useState<StatsPeriod>(() => ({
    preset: "next7",
    range: presetRange("next7"),
  }));
  const range = rangeIncludingFuture(period);
  const [view, setView] = useState<ViewFilter>("current");
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [kind, setKind] = useState<KindFilter>("all");
  const [data, setData] = useState<AssignmentsDTO | null>(null);
  const [selection, setSelection] = useState<SlotSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    const fetchRange = rangeIncludingFuture(period);
    const invalid = rangeError(fetchRange);
    if (invalid) {
      setError(invalid);
      return;
    }
    const current = ++request.current;
    setError(null);
    getSchedulerAssignments(fetchRange)
      .then((result) => {
        if (current === request.current) setData(result ?? null);
      })
      .catch((err) => {
        console.error("Error fetching assignments:", err);
        if (current === request.current) {
          setError("Errore nel recupero delle assegnazioni. Riprova.");
          setData(null);
        }
      });
  }, [period, getSchedulerAssignments]);

  const traces = (data?.traces ?? []).filter(
    (t) =>
      (view === "history" || t.state.kind === "current") &&
      (channel === "all" || t.channel === channel) &&
      (kind === "all" || t.kind === kind),
  );

  return (
    <Layout maxWClass="max-w-full">
      <AssignmentSlotModal
        selection={selection}
        onClose={() => setSelection(null)}
      />
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-2">
              <button
                onClick={() => navigate(CheckClosersAvailabilityScreenPath)}
                className="text-sm text-primary hover:underline"
              >
                ← Check Closer Availability
              </button>
              <h1 className="font-bold text-2xl">
                Assegnazioni dello scheduler
              </h1>
              <p className="text-sm text-gray-500 max-w-3xl">
                Per ogni slot, chi è stato scelto e perché. Clicca una cella per
                vedere i candidati di quello slot. "Assegnazioni attuali" mostra
                per ogni call solo l'ultima decisione ancora valida; "Tutta la
                cronologia" anche quelle poi riprogrammate, riassegnate o
                cancellate.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <PeriodSelector
                period={period}
                onChange={setPeriod}
                options={ASSIGNMENT_PERIOD_OPTIONS}
              />
              <div className="w-52 space-y-1">
                <p className="text-sm font-medium text-gray-700">Vista</p>
                <Select
                  value={view}
                  onChange={(id) => setView((id ?? "current") as ViewFilter)}
                  options={VIEW_OPTIONS}
                />
              </div>
              <div className="w-40 space-y-1">
                <p className="text-sm font-medium text-gray-700">Canale</p>
                <Select
                  value={channel}
                  onChange={(id) => setChannel((id ?? "all") as ChannelFilter)}
                  options={CHANNEL_OPTIONS}
                />
              </div>
              <div className="w-44 space-y-1">
                <p className="text-sm font-medium text-gray-700">Tipo</p>
                <Select
                  value={kind}
                  onChange={(id) => setKind((id ?? "all") as KindFilter)}
                  options={KIND_OPTIONS}
                />
              </div>
            </div>
          </div>
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
        </Card>

        {loading && !data && <LoadingBlock sizeClassName="h-64 w-full" />}

        {data && (
          <>
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
              <StatsKpi
                label="Assegnazioni"
                value={traces.length}
                sub="nel periodo e coi filtri"
              />
              <StatsKpi
                label="Primo saltato per cap"
                value={countOutcome(traces, "cap_skipped")}
                sub="il migliore era al cap"
              />
              <StatsKpi
                label="Tier 1 oltre il cap"
                value={countOutcome(traces, "tier1_overflow")}
                sub="tutti i liberi al cap"
              />
              <StatsKpi
                label="Ultima risorsa"
                value={countOutcome(traces, "last_resort")}
                sub="tutti al cap, nessun Tier 1"
              />
              <StatsKpi
                label="Scelte a mano"
                value={countOutcome(traces, "requested")}
                sub="closer scelto dal setter"
              />
            </div>
            <Card>
              <AssignmentsHeatmap
                traces={traces}
                fromDay={range.fromDay}
                toDay={range.toDay}
                showEmptyFutureDays={period.preset === "next7"}
                onSelect={setSelection}
              />
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
